/**
 * Wall + door edge rendering.
 *
 * Per WORLD_MODEL §4 + CLIENT_DESIGN §4.3 / §4.4, walls live on tile edges
 * identified by a canonical `EdgeKey { tile, axis }`. An edge between
 * `(x,y)` and `(x,y+1)` is `Horizontal`; an edge between `(x,y)` and
 * `(x+1,y)` is `Vertical`.
 *
 * In isometric screen space:
 *   - Horizontal edges render as line segments between the screen positions
 *     of `(x,y)` (top vertex) and `(x,y+1)` (bottom vertex) — i.e. from the
 *     bottom-edge of tile (x,y) to the top-edge of tile (x,y+1).
 *   - Vertical edges similarly run from the right-edge of tile (x,y) to
 *     the left-edge of tile (x+1,y).
 *
 * Phase 1 renders walls as solid-colour bars with a vertical "height" so
 * they read as structures, not as ground seams. Doors are rendered as a
 * shorter bar of the same colour with a vertical gap in the middle so the
 * passable slot is visually obvious. No pack atlas — procedural-only.
 */

import { Graphics } from 'pixi.js';

import type { WallEntry } from '../world/model';
import type { WorldModel } from '../world/model';
import {
  tileToIso,
  TILE_HEIGHT_PX,
  TILE_WIDTH_PX,
  visibleTileRange,
  type CameraState,
} from './iso';
import { wallMaterialColor } from './palette';
import type { SceneLayers } from './scene';

/** Walls on the perimeter of the local player's room render at this alpha
 *  so the viewer can see indoor content. CLIENT_DESIGN §4.5. */
const CUTAWAY_PERIMETER_ALPHA = 0.3;

const WALL_HEIGHT_PX = 192; // ~ 2 tiles tall at 128 px tile width
const WALL_THICKNESS = 20;

export interface WallRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
    cutawayRoomId: number,
  ) => void;
  destroy: () => void;
}

export function createWallRenderer(layers: SceneLayers): WallRenderer {
  // Two Graphics: opaque walls + cutaway (perimeter, lower alpha). Drawing
  // both under the same layer with distinct alphas avoids per-wall fill
  // churn.
  const gOpaque = new Graphics();
  const gFaded = new Graphics();
  layers.wallLayer.addChild(gOpaque, gFaded);

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
    cutawayRoomId: number,
  ): void => {
    gOpaque.clear();
    gFaded.clear();
    if (model.walls.size === 0) return;

    const r = visibleTileRange(cam, viewportW, viewportH, 2);
    const doCutaway = cutawayRoomId >= 0;

    for (const wall of model.walls.values()) {
      const [tx, ty] = wall.edge.tile;
      const nx = wall.edge.axis === 'Vertical' ? tx + 1 : tx;
      const ny = wall.edge.axis === 'Horizontal' ? ty + 1 : ty;
      if (tx < r.minX - 1 && nx < r.minX - 1) continue;
      if (tx > r.maxX + 1 && nx > r.maxX + 1) continue;
      if (ty < r.minY - 1 && ny < r.minY - 1) continue;
      if (ty > r.maxY + 1 && ny > r.maxY + 1) continue;

      const fade =
        doCutaway && isPerimeterOfRoom(model, wall, cutawayRoomId);
      drawWallOrDoor(fade ? gFaded : gOpaque, wall);
    }

    gFaded.alpha = CUTAWAY_PERIMETER_ALPHA;
  };

  return {
    render,
    destroy: () => {
      gOpaque.destroy();
      gFaded.destroy();
    },
  };
}

/**
 * A wall is on the perimeter of `roomId` if exactly one of the two tiles
 * it separates has `tileRoomIds[idx] === roomId` (the other is outside
 * the room, either in a different room or outdoors).
 *
 * Exported for unit testing.
 */
export function isPerimeterOfRoom(
  model: WorldModel,
  wall: WallEntry,
  roomId: number,
): boolean {
  const [tx, ty] = wall.edge.tile;
  const nx = wall.edge.axis === 'Vertical' ? tx + 1 : tx;
  const ny = wall.edge.axis === 'Horizontal' ? ty + 1 : ty;
  const w = model.width;
  const h = model.height;
  const inBoundsA = tx >= 0 && tx < w && ty >= 0 && ty < h;
  const inBoundsB = nx >= 0 && nx < w && ny >= 0 && ny < h;
  const aRoom = inBoundsA ? model.tileRoomIds[ty * w + tx] ?? -1 : -1;
  const bRoom = inBoundsB ? model.tileRoomIds[ny * w + nx] ?? -1 : -1;
  const aIs = aRoom === roomId;
  const bIs = bRoom === roomId;
  return (aIs && !bIs) || (!aIs && bIs);
}

function drawWallOrDoor(g: Graphics, wall: WallEntry): void {
  const isDoor = wall.occupant.kind === 'door';
  const color = wallMaterialColor(wall.occupant.material);
  const [tx, ty] = wall.edge.tile;

  // Anchor points for the two screen endpoints of the edge line.
  // Horizontal (separates y and y+1): from right-vertex of (x,y) to
  //   right-vertex of (x,y+1) — i.e. line runs top-right to bottom-right
  //   of the y-row diamond strip (down-right in screen space).
  // Vertical (separates x and x+1): from bottom-vertex of (x,y) to
  //   bottom-vertex of (x+1,y) — line runs bottom-right (up-right).
  const halfW = TILE_WIDTH_PX / 2;
  const halfH = TILE_HEIGHT_PX / 2;
  const a = tileToIso(tx, ty);
  let p1x: number;
  let p1y: number;
  let p2x: number;
  let p2y: number;
  if (wall.edge.axis === 'Horizontal') {
    // Edge between (tx, ty) and (tx, ty+1). Line from bottom vertex of
    // (tx, ty) to top vertex of (tx, ty+1) — both share the same point
    // (x * halfW - y * halfW, (x+y+1) * halfH) in iso space, so we use
    // the tile-edge midpoints instead: from (tx, ty)'s SW-edge midpoint
    // to (tx, ty)'s SE-edge midpoint — wait, that's the bottom edge of
    // the diamond, which IS the edge between y and y+1. OK:
    // Bottom-left-vertex of diamond = (a.x - halfW, a.y) — which is
    // actually the LEFT vertex. Bottom vertex is (a.x, a.y + halfH).
    // The edge between y and y+1 is the SE edge of tile (x,y), running
    // from the bottom vertex (a.x, a.y + halfH) to the right vertex
    // (a.x + halfW, a.y).
    p1x = a.x;
    p1y = a.y + halfH;
    p2x = a.x + halfW;
    p2y = a.y;
  } else {
    // Vertical edge between (tx, ty) and (tx+1, ty). This is the NE
    // edge of tile (x,y), running from top vertex (a.x, a.y - halfH)
    // to right vertex (a.x + halfW, a.y).
    p1x = a.x;
    p1y = a.y - halfH;
    p2x = a.x + halfW;
    p2y = a.y;
  }

  if (isDoor) {
    // Doorway: render the two end-stubs only, leaving the middle third
    // empty so the passable slot is obvious.
    const midAx = p1x + (p2x - p1x) * 0.33;
    const midAy = p1y + (p2y - p1y) * 0.33;
    const midBx = p1x + (p2x - p1x) * 0.67;
    const midBy = p1y + (p2y - p1y) * 0.67;
    drawEdgeBar(g, p1x, p1y, midAx, midAy, color);
    drawEdgeBar(g, midBx, midBy, p2x, p2y, color);
  } else {
    drawEdgeBar(g, p1x, p1y, p2x, p2y, color);
  }
}

/**
 * Draw a quad that stands up from the floor along the edge line. The quad
 * has two copies of the endpoints: one at ground level, one displaced
 * upward by WALL_HEIGHT_PX (negative Y in iso space).
 */
function drawEdgeBar(
  g: Graphics,
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  color: number,
): void {
  // Thicken the bar in screen Y by drawing a trapezoid with the top edge
  // offset upward by WALL_HEIGHT_PX.
  g.poly([
    p1x,
    p1y,
    p2x,
    p2y,
    p2x,
    p2y - WALL_HEIGHT_PX,
    p1x,
    p1y - WALL_HEIGHT_PX,
  ]);
  g.fill({ color, alpha: 1 });

  // Small highlight strip at the top edge for readability.
  g.poly([
    p1x,
    p1y - WALL_HEIGHT_PX,
    p2x,
    p2y - WALL_HEIGHT_PX,
    p2x,
    p2y - WALL_HEIGHT_PX + WALL_THICKNESS,
    p1x,
    p1y - WALL_HEIGHT_PX + WALL_THICKNESS,
  ]);
  g.fill({ color: lighten(color, 0.2), alpha: 1 });
}

function lighten(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return (lr << 16) | (lg << 8) | lb;
}
